// S-3 — `?ref=` capture for signup.
//
// The signup flow has multiple entry points (email/magic link, Google,
// Facebook, Apple) and several pages in between (login → verify → setup, or
// login → provider → /auth/callback). The referral code arrives once on the
// entry URL but the value needs to survive a Supabase OAuth round-trip and
// several client-side navigations before it can be attached to user_metadata
// at the moment the account is created. sessionStorage handles both cases
// without leaking across browsers or tabs.
//
// #1163 — how the code reaches the API. `ReferralsService.extractReferralCodeFromMetadata`
// reads `referred_by_code` off the Supabase user's metadata when the API mints
// the account row (`UserService.createUser`) and attaches the inviter there.
// So the web's whole job is to get the code INTO user_metadata before the
// first authenticated `GET /user`:
//
//   - email OTP: `signInWithOtp({ options: { data } })` — applied by Supabase
//     only when it creates the user, so it lands on exactly the sign-ups;
//   - OAuth: `auth.updateUser({ data })` in /auth/callback, awaited before the
//     redirect, the same seam #512 uses for the terms tick.
//
// `POST /me/referral/apply` (called from /me/invite) remains as the fallback
// for a code that missed both seams.

const STORAGE_KEY = 'wellmate.referralCode'

/** The Supabase user_metadata key the API reads (`referrals.service.ts`). */
export const REFERRED_BY_CODE_KEY = 'referred_by_code'

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function readSessionStorage(): StorageLike | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage
  } catch {
    // Safari private mode / disabled storage → silently no-op.
    return null
  }
}

/** Trim, upper-case and length-check a raw `?ref=` value; null when unusable. */
export function normaliseReferralCode(raw: string | null | undefined): string | null {
  const code = (raw ?? '').trim().toUpperCase()
  if (!code || code.length < 4 || code.length > 32) return null
  return code
}

/**
 * Read the entry URL's `?ref=` query param (if any), normalise it, and persist
 * it for later consumption at the signup seams. Safe to call on every mount —
 * a later visit with no `?ref=` never overwrites an earlier capture.
 *
 * `search` and `storage` are injectable for tests; the defaults are the
 * browser's.
 */
export function captureReferralFromUrl(
  search: string | null = typeof window === 'undefined' ? null : window.location.search,
  storage: StorageLike | null = readSessionStorage(),
): string | null {
  if (search == null) return null
  const code = normaliseReferralCode(new URLSearchParams(search).get('ref'))
  if (!code) return null
  storage?.setItem(STORAGE_KEY, code)
  return code
}

/**
 * Read (without clearing) the persisted referral code. Used at the signup
 * seams to populate user_metadata; cleared once the account exists.
 */
export function peekReferralCode(storage: StorageLike | null = readSessionStorage()): string | null {
  return storage?.getItem(STORAGE_KEY) ?? null
}

/**
 * Read + clear the persisted referral code. Call this after the code has been
 * handed off (to Supabase metadata, or to /me/referral/apply) — keeping it
 * would just risk re-attaching on a subsequent signup attempt in the same tab.
 */
export function consumeReferralCode(storage: StorageLike | null = readSessionStorage()): string | null {
  const code = storage?.getItem(STORAGE_KEY) ?? null
  storage?.removeItem(STORAGE_KEY)
  return code
}

/**
 * The metadata fragment to hand Supabase at account creation: the captured
 * code under the key the API reads, or nothing at all. Merged into whatever
 * else the sign-up writes (#512's `terms_accepted_at`), never replacing it.
 */
export function referralSignupMetadata(
  code: string | null = peekReferralCode(),
): { [REFERRED_BY_CODE_KEY]?: string } {
  const normalised = normaliseReferralCode(code)
  return normalised ? { [REFERRED_BY_CODE_KEY]: normalised } : {}
}
